import { useQuery } from "@tanstack/react-query";
import { compact } from "lodash/fp";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";

import type { Topic, TopicError, TopicResponse } from "@app/analytics/types";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import type { User } from "@app/common/types";
import { hasRetryableStatus } from "@app/common/utils";

const isTopicError = (response?: TopicResponse): response is TopicError =>
  response != null && "error" in response;

export const getQueryKey = ({
  topicId,
  userId,
  impersonateUserId,
}: {
  topicId?: Topic["id"];
  userId?: User["id"];
  impersonateUserId?: User["id"];
}) => {
  if (impersonateUserId) return ["topic", topicId, userId, impersonateUserId];
  if (userId) return ["topic", topicId, userId];
  return compact(["topic", topicId]);
};

const useTopicReport = () => {
  const { user } = useAuth();
  const { topicId } = useParams();
  const [queryParams] = useSearchParams();

  const impersonateUserId = queryParams.get("iid") ?? undefined;
  const isSharedReport =
    impersonateUserId != null && impersonateUserId !== `${user?.id}`;

  const topicReportQuery = useQuery({
    queryKey: getQueryKey({ topicId, userId: user?.id, impersonateUserId }),
    queryFn: () => {
      if (!topicId) return;
      return apiClient.topics.getTopic(topicId, impersonateUserId);
    },
    enabled: Boolean(topicId) && Boolean(user?.id),
    retry: (failureCount, error) =>
      failureCount < 3 && hasRetryableStatus(error),
  });

  const response = topicReportQuery.data;
  const responseIsError = isTopicError(response);

  return {
    topic: responseIsError ? undefined : response,
    responseError: responseIsError ? response : undefined,
    isLoading: topicReportQuery.isLoading,
    isError: topicReportQuery.isError || responseIsError,
    isSharedReport,
  };
};

export default useTopicReport;
